<template>
  <v-container class="py-8">
    <!-- Back navigation -->
    <v-row>
      <v-col cols="12">
        <v-btn
          variant="outlined"
          color="primary"
          size="small"
          @click="$router.push({ name: 'prompt-management' })"
          class="mb-6"
        >
          <template #prepend>
            <v-icon size="18">mdi-chevron-left</v-icon>
          </template>
          {{ $t('promptmanagement.backToPromptManagement') }}
        </v-btn>
      </v-col>
    </v-row>

    <!-- Page header -->
    <v-row>
      <v-col cols="12" class="pl-8">
        <h1 class="text-h4 font-weight-bold">{{ $t('promptmanagementtemplates.title') }}</h1>
        <p class="text-body-1 text-medium-emphasis mt-2">
          {{ $t('promptmanagementtemplates.description') }}
        </p>
      </v-col>
    </v-row>

    <!-- Domain dropdown + New Meeting Template button -->
    <v-row class="mt-6 px-2" align="center">
      <v-col cols="12" sm="4" md="3">
        <v-select
          v-model="selectedDomain"
          :items="domainOptions"
          item-title="title"
          item-value="value"
          :label="$t('generic.domain')"
          variant="outlined"
          color="primary"
          prepend-inner-icon="mdi-domain"
          density="comfortable"
          hide-details
          class="domain-select"
          @update:modelValue="onDomainChange"
        />
      </v-col>

      <v-spacer />

      <v-col cols="auto">
        <v-btn
          color="primary"
          variant="flat"
          prepend-icon="mdi-plus"
          size="large"
          @click="$router.push({ name: 'prompt-management-template-new', query: { domain: selectedDomain } })"
        >
          {{ $t('promptmanagementtemplates.newMeetingTemplate') }}
        </v-btn>
      </v-col>
    </v-row>

    <!-- Section: Meeting Template Management -->
    <v-row class="mt-8 px-2">
      <v-col cols="12">
        <v-card variant="outlined" rounded="lg" class="pa-6" style="border-color: #9E9E9E; background-color: white;">
          <h2 class="text-h5 font-weight-bold mb-1" style="color: #000;">{{ $t('promptmanagementtemplates.meetingTemplateManagement') }}</h2>
          <p class="text-body-2 mt-1 mb-6" style="color: #757575;">
            {{ $t('promptmanagementtemplates.meetingTemplateManagementHint') }}
          </p>
          <h3 class="text-subtitle-1 font-weight-bold mb-4" style="color: #000;">{{ $t('promptmanagementtemplates.availableMeetingTemplates') }}</h3>

          <!-- Scoped to just this table (not global Vuetify config) so the
               built-in pagination footer ("Items per page" etc.) follows
               the app locale without affecting any other page. Vuetify's
               own en/ja message bundles are passed explicitly since the
               root Vuetify config only registers "en". -->
          <v-locale-provider :locale="$i18n.locale" :messages="{ en: vuetifyEn, ja: vuetifyJa }">
          <v-data-table
            :headers="headers"
            :items="templates"
            :loading="loading"
            :loading-text="$t('promptmanagementtemplates.loadingTemplates')"
            :no-data-text="$t('promptmanagementtemplates.noTemplatesAvailable')"
            item-value="template_id"
            class="templates-table"
          >
            <template #headers="{ columns }">
              <tr class="table-header-row">
                <th
                  v-for="(col, i) in columns"
                  :key="col.key"
                  class="font-weight-bold text-black"
                  :style="i === 0 ? 'padding-left: 48px;' : (col.key === 'actions' ? 'text-align: center !important;' : '')"
                >
                  {{ col.title }}
                </th>
              </tr>
            </template>

            <template #item.template_name="{ item }">
              <div class="d-flex align-center ga-2">
                <div class="template-icon-bg">
                  <v-icon size="24" color="#0D3B6E">mdi-file-document-outline</v-icon>
                </div>
                <span class="font-weight-bold">{{ item.template_name?.length > 60 ? item.template_name.slice(0, 60) + '…' : item.template_name }}</span>
              </div>
            </template>

            <template #item.notes="{ item }">
              {{ item.notes?.length > 200 ? item.notes.slice(0, 200) + '…' : (item.notes || '—') }}
            </template>

            <template #item.modified_at="{ item }">
              {{ formatDate(item.modified_at) }}
            </template>

            <template #item.actions="{ item }">
              <div class="d-flex justify-end">
                <div class="d-flex align-center justify-space-between actions-container">
                  <v-btn variant="outlined" color="#9E9E9E" prepend-icon="mdi-eye-outline" class="action-btn action-btn--blue"
                    @click="$router.push({ name: 'prompt-management-template-view', params: { templateId: item.template_id }, query: { domain: selectedDomain } })">
                    {{ $t('generic.view') }}
                  </v-btn>
                  <v-btn v-if="canEditDelete(item)" variant="outlined" color="#9E9E9E" prepend-icon="mdi-pencil-outline" class="action-btn action-btn--blue"
                    @click="$router.push({ name: 'prompt-management-template-edit', params: { templateId: item.template_id }, query: { domain: selectedDomain } })">
                    {{ $t('generic.edit') }}
                  </v-btn>
                  <v-btn variant="outlined" color="#9E9E9E" prepend-icon="mdi-content-copy" class="action-btn action-btn--blue"
                    @click="handleDuplicateTemplate(item)">
                    {{ $t('generic.duplicate') }}
                  </v-btn>
                  <v-btn v-if="canEditDelete(item)" variant="outlined" color="#9E9E9E" prepend-icon="mdi-delete-outline" class="action-btn action-btn--red"
                    @click="confirmDelete(item)">
                    {{ $t('generic.delete') }}
                  </v-btn>
                </div>
              </div>
            </template>
          </v-data-table>
          </v-locale-provider>
        </v-card>
      </v-col>
    </v-row>

    <!-- Section: Template-based Prompts -->
    <v-row class="mt-6 px-2">
      <v-col cols="12">
        <v-card variant="outlined" rounded="lg" class="pa-6" style="border-color: #9E9E9E; background-color: white;">
          <div class="d-flex align-start justify-space-between mb-1">
            <div>
              <h2 class="text-h5 font-weight-bold" style="color: #000;">{{ $t('promptmanagementtemplates.outputPromptManagement') }}</h2>
              <p class="text-body-2 mt-1 mb-4" style="color: #757575;">
                {{ $t('promptmanagementtemplates.outputPromptManagementHint') }}
              </p>
            </div>
            <v-btn
              color="primary"
              variant="flat"
              prepend-icon="mdi-plus"
              class="flex-shrink-0 ml-4 mt-1"
              @click="openNewPromptDialog"
            >
              {{ $t('promptmanagement.newPrompt') }}
            </v-btn>
          </div>

          <!-- Type filter tabs -->
          <v-tabs v-model="selectedPromptType" color="primary" class="mb-4" @update:modelValue="loadPrompts">
            <v-tab value="all">{{ $t('promptmanagement.allTypes') }}</v-tab>
            <v-tab v-for="type in promptTypes" :key="type.value" :value="type.value">
              {{ type.label }}
            </v-tab>
          </v-tabs>

          <v-divider class="mb-4" />

          <!-- Scoped to just this table (not global Vuetify config) so the
               built-in pagination footer ("Items per page" etc.) follows
               the app locale without affecting any other page. Vuetify's
               own en/ja message bundles are passed explicitly since the
               root Vuetify config only registers "en". -->
          <v-locale-provider :locale="$i18n.locale" :messages="{ en: vuetifyEn, ja: vuetifyJa }">
          <v-data-table
            :headers="promptHeaders"
            :items="prompts"
            :loading="promptsLoading"
            :loading-text="$t('promptmanagement.loadingPrompts')"
            :no-data-text="$t('promptmanagement.noPromptsAvailable')"
            item-value="prompt_id"
            class="templates-table"
          >
            <template #headers="{ columns }">
              <tr class="table-header-row">
                <th
                  v-for="(col, i) in columns"
                  :key="col.key"
                  class="font-weight-bold text-black"
                  :style="i === 0 ? 'padding-left: 48px;' : (col.key === 'actions' ? 'text-align: center !important;' : '')"
                >
                  {{ col.title }}
                </th>
              </tr>
            </template>

            <template #item.name="{ item }">
              <div class="d-flex align-center ga-2">
                <div class="prompt-icon-bg" :style="{ backgroundColor: typeColorLight(item.prompt_type) }">
                  <v-icon size="20" :color="typeColor(item.prompt_type)">mdi-text-box-outline</v-icon>
                </div>
                <span class="font-weight-bold">{{ item.name?.length > 60 ? item.name.slice(0, 60) + '…' : item.name }}</span>
              </div>
            </template>

            <template #item.prompt_type="{ item }">
              <v-chip size="small" :color="typeColor(item.prompt_type)" variant="tonal">
                {{ typeLabel(item.prompt_type) }}
              </v-chip>
            </template>

            <template #item.prompt_content="{ item }">
              <span class="text-body-2 text-medium-emphasis">
                {{ item.prompt_content?.length > 200 ? item.prompt_content.slice(0, 200) + '…' : (item.prompt_content || '—') }}
              </span>
            </template>

            <template #item.actions="{ item }">
              <div class="d-flex justify-end">
                <div class="d-flex align-center justify-space-between actions-container">
                  <v-btn variant="outlined" color="#9E9E9E" prepend-icon="mdi-eye-outline" class="action-btn action-btn--blue"
                    @click="openViewDialog(item)">{{ $t('generic.view') }}</v-btn>
                  <v-btn v-if="canEditDelete(item)" variant="outlined" color="#9E9E9E" prepend-icon="mdi-pencil-outline" class="action-btn action-btn--blue"
                    @click="openEditPromptDialog(item)">{{ $t('generic.edit') }}</v-btn>
                  <v-btn variant="outlined" color="#9E9E9E" prepend-icon="mdi-content-copy" class="action-btn action-btn--blue"
                    @click="handleCopyPrompt(item)">{{ $t('generic.duplicate') }}</v-btn>
                  <v-btn v-if="canEditDelete(item)" variant="outlined" color="#9E9E9E" prepend-icon="mdi-delete-outline" class="action-btn action-btn--red"
                    @click="confirmDeletePrompt(item)">{{ $t('generic.delete') }}</v-btn>
                </div>
              </div>
            </template>
          </v-data-table>
          </v-locale-provider>
        </v-card>
      </v-col>
    </v-row>

    <!-- Delete template confirmation dialog -->
    <v-dialog v-model="deleteDialog" max-width="400">
      <v-card>
        <v-card-title class="text-h6">{{ $t('promptmanagementtemplates.deleteTemplateTitle') }}</v-card-title>
        <v-card-text>{{ $t('promptmanagement.confirmDeleteNamed', { name: templateToDelete?.template_name }) }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="deleteDialog = false">{{ $t('generic.cancel') }}</v-btn>
          <v-btn color="red" variant="tonal" @click="handleDelete">{{ $t('generic.delete') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete prompt confirmation dialog -->
    <v-dialog v-model="deletePromptDialog" max-width="400">
      <v-card>
        <v-card-title class="text-h6">{{ $t('promptmanagement.deletePromptTitle') }}</v-card-title>
        <v-card-text>{{ $t('promptmanagement.confirmDeleteNamed', { name: promptToDelete?.name }) }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="deletePromptDialog = false">{{ $t('generic.cancel') }}</v-btn>
          <v-btn color="red" variant="tonal" @click="handleDeletePrompt">{{ $t('generic.delete') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- View Prompt dialog -->
    <v-dialog v-model="viewDialog" max-width="560">
      <v-card v-if="viewingPrompt" rounded="lg">
        <div class="d-flex align-center ga-3 px-6 pt-6 pb-4" style="border-bottom: 1px solid #E0E0E0;">
          <div class="d-flex align-center justify-center flex-shrink-0"
            style="width: 44px; height: 44px; border-radius: 8px;"
            :style="{ backgroundColor: typeColorLight(viewingPrompt.prompt_type) }">
            <v-icon size="24" :color="typeColor(viewingPrompt.prompt_type)">mdi-text-box-outline</v-icon>
          </div>
          <div class="flex-grow-1">
            <div class="text-h6 font-weight-bold" style="line-height: 1.2;">{{ viewingPrompt.name }}</div>
            <v-chip size="x-small" :color="typeColor(viewingPrompt.prompt_type)" variant="tonal" class="mt-1">
              {{ typeLabel(viewingPrompt.prompt_type) }}
            </v-chip>
          </div>
          <v-btn icon variant="text" size="small" @click="viewDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
        <v-card-text class="px-6 pt-5 pb-4">
          <div class="mb-4">
            <div style="font-size: 0.72rem; font-weight: 700; color: #9E9E9E; letter-spacing: 0.07em; text-transform: uppercase;" class="mb-1">{{ $t('generic.domain') }}</div>
            <span class="text-body-1">{{ domainDisplayLabel(viewingPrompt.domain) }}</span>
          </div>
          <div>
            <div style="font-size: 0.72rem; font-weight: 700; color: #9E9E9E; letter-spacing: 0.07em; text-transform: uppercase;" class="mb-2">{{ $t('promptmanagement.promptContent') }}</div>
            <div class="pa-4 text-body-2"
              style="background-color: #F8F9FA; border: 1px solid #E0E0E0; border-radius: 8px; white-space: pre-wrap; line-height: 1.75; max-height: 320px; overflow-y: auto; color: #212121;">
              {{ viewingPrompt.prompt_content }}
            </div>
          </div>
        </v-card-text>
        <v-card-actions class="px-6 pb-5 pt-0">
          <v-spacer />
          <v-btn variant="text" @click="viewDialog = false">{{ $t('generic.close') }}</v-btn>
          <v-btn v-if="canEditDelete(viewingPrompt)" style="background-color: #0D3B6E; color: white;" prepend-icon="mdi-pencil-outline"
            @click="viewDialog = false; openEditPromptDialog(viewingPrompt)">
            {{ $t('generic.edit') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- New / Edit Prompt dialog -->
    <v-dialog v-model="promptDialog" max-width="560" persistent>
      <v-card rounded="lg">
        <v-card-title class="text-h6 pt-6 px-6">{{ promptEditMode ? $t('promptmanagement.editPrompt') : $t('promptmanagement.newPrompt') }}</v-card-title>
        <v-card-text class="px-6 pb-2">
          <label class="field-label mb-1 d-block">{{ $t('generic.domain') }}</label>
          <v-select :model-value="selectedDomain" :items="domainOptions" item-title="title" item-value="value"
            variant="outlined" color="primary" prepend-inner-icon="mdi-domain" density="comfortable" disabled hide-details class="mb-4" />

          <label class="field-label mb-1 d-block">{{ $t('promptmanagement.promptType') }} <span class="text-red font-weight-bold">*</span></label>
          <v-select v-model="promptForm.prompt_type" :items="promptTypeOptions" item-title="label" item-value="value"
            variant="outlined" color="primary" density="comfortable" :placeholder="$t('promptmanagement.selectPromptType')"
            :error="promptFormError && !promptForm.prompt_type" hide-details class="mb-4" />

          <label class="field-label mb-1 d-block">{{ $t('promptmanagement.promptName') }} <span class="text-red font-weight-bold">*</span></label>
          <v-text-field v-model="promptForm.name" variant="outlined" color="primary" density="comfortable"
            :placeholder="$t('promptmanagement.enterPromptName')" maxlength="100"
            :error="promptFormError && !promptForm.name.trim()" hide-details class="mb-1">
            <template #append-inner>
              <span class="char-counter">{{ promptForm.name.length }} / 100</span>
            </template>
          </v-text-field>
          <div class="mb-4" />

          <label class="field-label mb-1 d-block">{{ $t('promptmanagement.promptContent') }} <span class="text-red font-weight-bold">*</span></label>
          <v-textarea v-model="promptForm.prompt_content" variant="outlined" color="primary" density="comfortable"
            rows="5" :placeholder="$t('promptmanagement.enterPromptContent')" maxlength="10000"
            :error="promptFormError && !promptForm.prompt_content.trim()" hide-details class="mb-1">
            <template #append-inner>
              <span class="char-counter" style="align-self: flex-end; padding-bottom: 2px;">{{ promptForm.prompt_content.length }} / 10000</span>
            </template>
          </v-textarea>

          <div v-if="promptFormError" class="mt-3 text-center">
            <span class="text-body-2 font-weight-medium" style="color: #D32F2F;">{{ $t('personamanagement.fillRequiredFields') }}</span>
          </div>
        </v-card-text>
        <v-card-actions class="px-6 pb-6 pt-2">
          <v-spacer />
          <v-btn variant="outlined" color="#0D3B6E" style="background-color: white;" @click="closePromptDialog">{{ $t('generic.cancel') }}</v-btn>
          <v-btn style="background-color: #0D3B6E; color: white;" :loading="savingPrompt" @click="handleSavePrompt">
            {{ promptEditMode ? $t('personaframework.saveChanges') : $t('promptmanagement.createPrompt') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbars -->
    <v-snackbar v-model="copyPromptSnackbar" color="success" location="bottom right" :timeout="2000">
      <v-icon class="mr-2">mdi-check-circle-outline</v-icon>{{ $t('promptmanagement.promptDuplicatedSuccess') }}
    </v-snackbar>
    <v-snackbar v-model="promptSuccessSnackbar" color="success" location="bottom right" :timeout="2500">
      <v-icon class="mr-2">mdi-check-circle-outline</v-icon>{{ promptSuccessMessage }}
    </v-snackbar>
  </v-container>
</template>

<script>
import { fetch_meeting_templates, delete_meeting_template, fetch_prompts, create_prompt, update_prompt, delete_prompt, copy_prompt, get_current_user_from_storage, build_domain_options, get_default_domain } from '@/api';
import { en as vuetifyEn, ja as vuetifyJa } from 'vuetify/locale';
import { formatDate as formatLocalizedDate } from '@/utils/dateFormat';
import { stepShowsAttachment } from '@/utils/meetingTemplateSteps';

export default {
  name: 'PromptManagementTemplates',

  data() {
    return {
      // Vuetify's own locale message bundles, passed explicitly to the
      // page-scoped v-locale-provider below (the root Vuetify config only
      // registers "en", so "ja" must be supplied here for the data-tables'
      // built-in pagination text to localize).
      vuetifyEn,
      vuetifyJa,
      selectedDomain: get_default_domain(),
      currentUser: get_current_user_from_storage(),
      // Templates
      templates: [],
      loading: false,
      deleteDialog: false,
      templateToDelete: null,
      // Prompts
      prompts: [],
      promptsLoading: false,
      selectedPromptType: 'all',
      // Prompt CRUD
      deletePromptDialog: false,
      promptToDelete: null,
      viewDialog: false,
      viewingPrompt: null,
      promptDialog: false,
      promptFormError: false,
      savingPrompt: false,
      promptEditMode: false,
      editingPromptId: null,
      promptForm: { name: '', prompt_type: null, prompt_content: '' },
      copyPromptSnackbar: false,
      promptSuccessSnackbar: false,
      promptSuccessMessage: '',
    };
  },

  computed: {
    isSuperAdmin() {
      return this.currentUser?.role_id === 'super_admin';
    },
    domainOptions() {
      return build_domain_options(this.$t);
    },
    promptTypes() {
      return [
        { value: 'summarise', label: this.$t('promptmanagement.summariseType') },
        { value: 'improvement_plan', label: this.$t('focusgroup.improvementPlan') },
      ];
    },
    promptTypeOptions() {
      return [
        { value: 'summarise', label: this.$t('promptmanagement.summariseType') },
        { value: 'improvement_plan', label: this.$t('focusgroup.improvementPlan') },
      ];
    },
    headers() {
      return [
        { title: this.$t('promptmanagementtemplates.columnTemplateName'), key: 'template_name', sortable: true, align: 'start', width: '20%' },
        { title: this.$t('personagrid.description'), key: 'notes', sortable: false, align: 'start', width: '35%' },
        { title: this.$t('promptmanagementtemplates.columnLastUpdated'), key: 'modified_at', sortable: true, align: 'start', width: '15%' },
        { title: this.$t('generic.actions'), key: 'actions', sortable: false, align: 'center', width: '30%' },
      ];
    },
    promptHeaders() {
      return [
        { title: this.$t('promptmanagement.columnPromptName'), key: 'name', sortable: true, align: 'start', width: '22%' },
        { title: this.$t('personaframework.columnType'), key: 'prompt_type', sortable: true, align: 'start', width: '15%' },
        { title: this.$t('promptmanagement.columnPreview'), key: 'prompt_content', sortable: false, align: 'start', width: '33%' },
        { title: this.$t('generic.actions'), key: 'actions', sortable: false, align: 'center', width: '30%' },
      ];
    },
  },

  async mounted() {
    await this.loadTemplates();
    await this.loadPrompts();
  },

  methods: {
    isDefaultItem(item) {
      return item == null || item.group_id === null || item.group_id === undefined;
    },
    canEditDelete(item) {
      if (this.isDefaultItem(item)) return this.isSuperAdmin;
      return true;
    },
    onDomainChange() {
      this.loadTemplates();
      this.loadPrompts();
    },

    // ── Templates ──────────────────────────────────────
    async loadTemplates() {
      this.loading = true;
      try {
        const requestParams = { domain: this.selectedDomain };
        const response = await fetch_meeting_templates(requestParams);
        this.templates = response.data;
      } catch (error) {
        console.error('Failed to load meeting templates:', error);
        this.templates = [];
      } finally {
        this.loading = false;
      }
    },

    formatDate(dateStr) {
      return formatLocalizedDate(dateStr, this.$i18n.locale);
    },

    confirmDelete(item) {
      this.templateToDelete = item;
      this.deleteDialog = true;
    },

    async handleDelete() {
      this.deleteDialog = false;
      try {
        await delete_meeting_template(this.templateToDelete.template_id);
        const idx = this.templates.findIndex(t => t.template_id === this.templateToDelete.template_id);
        if (idx !== -1) this.templates.splice(idx, 1);
      } catch (error) {
        console.error('Failed to delete template:', error);
      } finally {
        this.templateToDelete = null;
      }
    },

    // ── Prompts ────────────────────────────────────────
    async loadPrompts() {
      this.promptsLoading = true;
      try {
        const params = { domain: this.selectedDomain, execution_mode: 'template_based' };
        if (this.selectedPromptType !== 'all') params.prompt_type = this.selectedPromptType;
        const response = await fetch_prompts(params);
        const data = response.data;
        this.prompts = Array.isArray(data) ? data : (data?.items ?? data?.prompts ?? []);
      } catch (error) {
        console.error('Failed to load prompts:', error);
        this.prompts = [];
      } finally {
        this.promptsLoading = false;
      }
    },

    typeLabel(type) {
      const found = this.promptTypes.find(t => t.value === type);
      return found ? found.label : type;
    },
    typeColor(type) {
      return { question_generation: '#0D3B6E', summarise: '#1B5E20', improvement_plan: '#E65100' }[type] || '#757575';
    },
    typeColorLight(type) {
      return { question_generation: '#BBDEFB', summarise: '#C8E6C9', improvement_plan: '#FFE0B2' }[type] || '#F5F5F5';
    },
    domainDisplayLabel(domain) {
      const found = this.domainOptions.find(d => d.value === domain);
      return found ? found.title : (domain || '—');
    },

    openViewDialog(item) { this.viewingPrompt = item; this.viewDialog = true; },

    openNewPromptDialog() {
      this.promptEditMode = false;
      this.editingPromptId = null;
      this.promptForm = { name: '', prompt_type: null, prompt_content: '' };
      this.promptFormError = false;
      this.promptDialog = true;
    },
    openEditPromptDialog(item) {
      this.promptEditMode = true;
      this.editingPromptId = item.prompt_id;
      this.promptForm = { name: item.name || '', prompt_type: item.prompt_type || null, prompt_content: item.prompt_content || '' };
      this.promptFormError = false;
      this.promptDialog = true;
    },
    closePromptDialog() {
      this.promptDialog = false;
      this.promptFormError = false;
      this.promptEditMode = false;
      this.editingPromptId = null;
    },

    async handleSavePrompt() {
      const { name, prompt_type, prompt_content } = this.promptForm;
      if (!name.trim() || !prompt_type || !prompt_content.trim()) { this.promptFormError = true; return; }
      this.promptFormError = false;
      this.savingPrompt = true;
      try {
        if (this.promptEditMode && this.editingPromptId) {
          await update_prompt(this.editingPromptId, { name: name.trim(), prompt_type, prompt_content: prompt_content.trim() });
        } else {
          const groupId = this.isSuperAdmin ? 'nttdata' : (this.currentUser?.group_ids?.[0] ?? null);
          await create_prompt({ name: name.trim(), prompt_type, prompt_content: prompt_content.trim(), domain: this.selectedDomain, execution_mode: 'template_based', group_id: groupId });
        }
        this.promptSuccessMessage = this.promptEditMode ? this.$t('promptmanagement.promptUpdatedSuccess') : this.$t('promptmanagement.promptAddedSuccess');
        this.promptDialog = false;
        this.promptSuccessSnackbar = true;
        await this.loadPrompts();
      } catch (error) {
        console.error('Failed to save prompt:', error.response?.data || error);
      } finally {
        this.savingPrompt = false;
        this.promptEditMode = false;
        this.editingPromptId = null;
      }
    },

    async handleDuplicateTemplate(item) {
      try {
        const { get_meeting_template, create_meeting_template } = await import('@/api');
        const response = await get_meeting_template(item.template_id);
        const t = response.data;
        const groupId = this.isSuperAdmin ? 'nttdata' : (this.currentUser?.group_ids?.[0] ?? null);
        await create_meeting_template({
          template_name: this.$t('promptmanagement.copyOfName', { name: t.template_name || '' }),
          domain_id: t.domain_id,
          notes: t.notes || '',
          steps: (t.steps || []).map(s => ({
            step_id: crypto.randomUUID(),
            order: s.order,
            step_name: s.step_name,
            facilitator_prompt: s.facilitator_prompt,
            persona_prompt: s.persona_prompt,
            max_rounds: s.max_rounds,
            facilitator_completion_condition: s.facilitator_completion_condition,
            show_attachment: stepShowsAttachment(s),
          })),
          group_id: groupId,
        });
        await this.loadTemplates();
        this.copyPromptSnackbar = true;
      } catch (error) {
        console.error('Failed to duplicate template:', error);
      }
    },

    confirmDeletePrompt(item) { this.promptToDelete = item; this.deletePromptDialog = true; },
    async handleDeletePrompt() {
      this.deletePromptDialog = false;
      try {
        await delete_prompt(this.promptToDelete.prompt_id);
        const idx = this.prompts.findIndex(p => p.prompt_id === this.promptToDelete.prompt_id);
        if (idx !== -1) this.prompts.splice(idx, 1);
      } catch (error) {
        console.error('Failed to delete prompt:', error);
      } finally {
        this.promptToDelete = null;
      }
    },

    async handleCopyPrompt(item) {
      try {
        const { get_prompt } = await import('@/api');
        const response = await get_prompt(item.prompt_id);
        const p = response.data;
        const groupId = this.isSuperAdmin ? 'nttdata' : (this.currentUser?.group_ids?.[0] ?? null);
        const payload = {
          name: this.$t('promptmanagement.copyOfName', { name: p.name || '' }),
          prompt_type: p.prompt_type,
          prompt_content: p.prompt_content,
          domain: p.domain,
          execution_mode: p.execution_mode,
          group_id: groupId,
        };
        await create_prompt(payload);
        await this.loadPrompts();
        this.copyPromptSnackbar = true;
      } catch (error) {
        console.error('Failed to copy prompt:', error);
      }
    },
  },
};
</script>

<style scoped>
.domain-select { max-width: 300px; }

.templates-table {
  border: 1px solid #E0E0E0;
  border-radius: 8px;
}

.template-icon-bg {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: #BBDEFB;
  border-radius: 6px;
  padding: 4px;
  flex-shrink: 0;
}

.prompt-icon-bg {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  padding: 4px;
  flex-shrink: 0;
}

.action-btn { height: 32px !important; font-size: 0.75rem !important; }

.action-btn--blue :deep(.v-btn__content),
.action-btn--blue :deep(.v-icon) { color: #0D3B6E !important; }

.action-btn--red :deep(.v-btn__content),
.action-btn--red :deep(.v-icon) { color: #D32F2F !important; }

.table-header-row th {
  background-color: #F5F5F5 !important;
  padding-top: 12px !important;
  padding-bottom: 12px !important;
  text-align: left !important;
}

.templates-table :deep(tbody td) {
  padding-top: 14px !important;
  padding-bottom: 14px !important;
}

.actions-container {
  padding: 6px 4px;
  gap: 6px;
}

.templates-table :deep(.v-data-table-footer) {
  justify-content: center !important;
}

.field-label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #424242;
}

.char-counter {
  font-size: 0.72rem;
  color: #9E9E9E;
  white-space: nowrap;
  align-self: flex-end;
  padding-bottom: 2px;
}
</style>
